import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import AdminService from '../../services/adminService';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip
} from '@mui/material';
import {
  People as PeopleIcon,
  FitnessCenter as ExerciseIcon,
  ShowChart as StatsIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';

// Import our custom components
import PageHeader from '../layout/PageHeader';
import DataCard from '../common/DataCard';
import EmptyState from '../common/EmptyState';
import Loading from '../layout/Loading';

const AdminDashboard = () => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalExercises: 0,
    totalWorkouts: 0,
    activeUsers: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        // Fetch admin stats using AdminService
        const statsData = await AdminService.getStatsOverview(token);
        console.log('Admin stats data:', statsData);

        if (statsData.success) {
          setStats({
            totalUsers: statsData.stats.totalUsers || 0,
            totalExercises: statsData.stats.totalExercises || 0,
            totalWorkouts: statsData.stats.totalWorkouts || 0,
            activeUsers: statsData.stats.activeUsers || 0
          });
        }

        // Fetch recent users using AdminService
        const usersData = await AdminService.getUsers({ limit: 5, sort: 'createdAt' }, token);
        console.log('Admin users data:', usersData);

        if (usersData.success) {
          setRecentUsers(usersData.users || []);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        toast.error('Failed to load admin dashboard data');
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [token]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <Loading message="Loading admin dashboard..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Admin Dashboard"
        subtitle="Manage your gym application platform"
        breadcrumbs={[
          { label: 'Admin', link: '/admin' }
        ]}
      />
      
      {/* Quick Actions */}
      <Paper 
        elevation={0} 
        variant="outlined" 
        sx={{ p: 2, mb: 4, borderRadius: 2 }}
      >
        <Typography variant="h6" gutterBottom sx={{ pl: 1 }}>
          Quick Actions
        </Typography>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2}
          sx={{ p: 1 }}
        >
          <Button 
            component={RouterLink} 
            to="/admin/exercises" 
            variant="contained" 
            startIcon={<ExerciseIcon />}
            fullWidth
          >
            Manage Exercises
          </Button>
          <Button 
            component={RouterLink} 
            to="/admin/users" 
            variant="outlined"
            startIcon={<PeopleIcon />}
            fullWidth
          >
            Manage Users
          </Button>
          <Button 
            component={RouterLink} 
            to="/admin/stats" 
            variant="outlined"
            startIcon={<StatsIcon />}
            fullWidth
          >
            View Statistics
          </Button>
        </Stack>
      </Paper>
      
      {/* Stats Overview */}
      <Typography variant="h6" gutterBottom>
        Platform Overview
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <DataCard
            title="Total Users"
            value={stats.totalUsers}
            icon={<PeopleIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DataCard
            title="Active Users"
            value={stats.activeUsers}
            icon={<PersonIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DataCard
            title="Total Exercises"
            value={stats.totalExercises}
            icon={<ExerciseIcon />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DataCard
            title="Total Workouts"
            value={stats.totalWorkouts}
            icon={<StatsIcon />}
            color="warning"
          />
        </Grid>
      </Grid>
      
      {/* Recent Users */}
      <Typography variant="h6" gutterBottom>
        Recently Joined Users
      </Typography>
      {recentUsers.length > 0 ? (
        <Paper elevation={0} variant="outlined" sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Joined Date</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentUsers.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ mr: 2, bgcolor: user.role === 'admin' ? 'primary.main' : 'secondary.main' }}
                        >
                          {user?.name?.charAt(0)}
                        </Avatar>
                        {user?.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        {user.email}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        {formatDate(user.createdAt)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.role} 
                        color={user.role === 'admin' ? 'primary' : 'default'} 
                        size="small"
                        variant={user.role === 'admin' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        component={RouterLink}
                        to={`/admin/users/${user._id}`}
                        startIcon={<ViewIcon />}
                        size="small"
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Box sx={{ mb: 4 }}>
          <EmptyState
            title="No users found"
            description="There are no users registered in the system yet"
            icon={<PeopleIcon />}
          />
        </Box>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button 
          component={RouterLink} 
          to="/admin/users" 
          variant="outlined"
          startIcon={<PeopleIcon />}
        >
          View All Users
        </Button>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
